/**
 * Cartel de bolsas del depósito.
 *
 * 2026-09-10 — depósito con dos correos: en modo `multi` muestra UNA tarjeta por correo activo.
 * Cada tarjeta es autosuficiente (pedido del usuario): tiene su propio campo para escanear la
 * bolsa de ESE correo (abrir o cambiar) y su botón "Cerrar bolsa {correo}". No hay campo suelto.
 * En modo `simple` es la tarjeta única de siempre.
 * Fuente: GET /api/warehouse/bags/active → { bags, mode, couriers, missing, bag }.
 */
import React, { useState, useEffect } from 'react';
import { Can } from '../../../lib/rbac';
import type { ActiveBagsView, ActiveBagView, WhCourier } from '../shared';

function minutesOpen(openedAt: string): number {
  return Math.floor((Date.now() - new Date(openedAt).getTime()) / 60000);
}

/** Campo de escaneo de bolsa (autolimpia al enviar). */
function BagScanForm({
  placeholder,
  color,
  onScan,
  autoFocus,
}: {
  placeholder: string;
  color: string;
  onScan: (code: string) => void;
  autoFocus?: boolean;
}) {
  const [value, setValue] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const code = value.trim();
        if (!code) return;
        onScan(code);
        setValue('');
      }}
      className="mt-2 flex gap-2"
    >
      <input
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="flex-1 rounded border border-gray-300 bg-[var(--color-bg)] px-2 py-1 font-mono text-sm text-[var(--color-text)]"
      />
      <button
        type="submit"
        className="rounded px-3 py-1 text-sm font-semibold text-white hover:opacity-90"
        style={{ backgroundColor: color }}
      >
        Abrir
      </button>
    </form>
  );
}

function CourierCard({
  bag,
  courier,
  onClose,
  onScan,
}: {
  bag: ActiveBagView | null;
  courier: WhCourier;
  onClose: (bagId: number) => void;
  onScan: (code: string, courierKey: string) => void;
}) {
  const color = courier.color;
  if (!bag) {
    return (
      <div
        className="rounded border-l-4 bg-yellow-50 px-4 py-3 dark:bg-yellow-900/20"
        style={{ borderLeftColor: color }}
      >
        <div className="text-xs font-bold uppercase tracking-wider" style={{ color }}>
          {courier.name}
        </div>
        <div className="mt-1 font-semibold text-yellow-900 dark:text-yellow-200">
          ⚠️ Sin bolsa abierta
        </div>
        <Can cap="warehouse.operate">
          <BagScanForm
            placeholder={`Escaneá la bolsa para ${courier.name}…`}
            color={color}
            onScan={(code) => onScan(code, courier.key)}
          />
        </Can>
      </div>
    );
  }
  return (
    <div
      className="rounded border-l-4 bg-[var(--color-surface)] px-4 py-3"
      style={{ borderLeftColor: color }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider" style={{ color }}>
            {courier.name}
          </div>
          <div className="mt-0.5 text-lg font-bold text-[var(--color-text)]">
            📦 Bolsa <span className="font-mono">{bag.bag_code}</span>
          </div>
          <div className="mt-0.5 text-sm text-[var(--color-text-secondary)]">
            {bag.order_count} órdenes · {bag.item_count} productos · abierta hace{' '}
            {minutesOpen(bag.opened_at)} min
          </div>
        </div>
        <Can cap="warehouse.operate">
          <button
            onClick={() => onClose(bag.id)}
            className="shrink-0 rounded px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: color }}
          >
            Cerrar bolsa {courier.name}
          </button>
        </Can>
      </div>
      <Can cap="warehouse.operate">
        <BagScanForm
          placeholder={`Escaneá otra bolsa de ${courier.name} para cambiar…`}
          color={color}
          onScan={(code) => onScan(code, courier.key)}
        />
      </Can>
    </div>
  );
}

export function BagBanner({
  onScan,
  onClose,
  refreshKey,
}: {
  /** courierKey: correo de la tarjeta desde la que se escaneó (modo multi). */
  onScan: (code: string, courierKey?: string | null) => void;
  onClose: (bagId: number) => void;
  refreshKey?: number;
}) {
  const [view, setView] = useState<ActiveBagsView | null>(null);

  useEffect(() => {
    fetch(`/api/warehouse/bags/active`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setView(d))
      .catch(() => setView(null));
  }, [refreshKey]);

  const bags = view?.bags ?? [];
  const mode = view?.mode ?? 'simple';

  // ── Modo multi: una tarjeta por correo activo, cada una con su escaneo y su cierre ──
  if (mode === 'multi' && view) {
    const unassigned = bags.filter((b) => b.courier_id === null);
    return (
      <div className="mb-4 space-y-2">
        <div className="grid gap-2 md:grid-cols-2">
          {view.couriers.map((c) => (
            <CourierCard
              key={c.id}
              courier={c}
              bag={bags.find((b) => b.courier_id === c.id) ?? null}
              onClose={onClose}
              onScan={onScan}
            />
          ))}
          {unassigned.map((b) => (
            <div
              key={b.id}
              className="rounded border-l-4 border-gray-400 bg-[var(--color-surface)] px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Bolsa sin correo
                  </div>
                  <div className="mt-0.5 text-lg font-bold text-[var(--color-text)]">
                    📦 Bolsa <span className="font-mono">{b.bag_code}</span>
                  </div>
                  <div className="mt-0.5 text-sm text-[var(--color-text-secondary)]">
                    El primer paquete que entre le fija el correo
                  </div>
                </div>
                <Can cap="warehouse.operate">
                  <button
                    onClick={() => onClose(b.id)}
                    className="shrink-0 rounded bg-gray-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-700"
                  >
                    Cerrar bolsa
                  </button>
                </Can>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Modo simple (o sin datos): tarjeta única, como siempre ──
  const activeBag = view?.bag ?? null;
  if (!activeBag) {
    return (
      <div className="mb-4 rounded border-l-4 border-yellow-500 bg-yellow-50 px-4 py-3">
        <div className="font-semibold text-yellow-900">⚠️ SIN BOLSA ACTIVA</div>
        <div className="mt-1 text-sm text-gray-700">Escaneá una bolsa antes de seguir empacando.</div>
        <Can cap="warehouse.operate">
          <BagScanForm
            autoFocus
            placeholder="Escaneá código de bolsa..."
            color="#ca8a04"
            onScan={(code) => onScan(code)}
          />
        </Can>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded border-l-4 border-green-500 bg-green-50 px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-green-900">
            📦 BOLSA ACTIVA: {activeBag.bag_code}
            {activeBag.courier ? (
              <span className="ml-2 text-xs font-bold uppercase" style={{ color: activeBag.courier.color }}>
                {activeBag.courier.name}
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-sm text-gray-700">
            {activeBag.order_count} órdenes · {activeBag.item_count} productos · abierta hace{' '}
            {minutesOpen(activeBag.opened_at)} min
          </div>
        </div>
        <Can cap="warehouse.operate">
          <button
            onClick={() => onClose(activeBag.id)}
            className="rounded bg-gray-700 px-3 py-1 text-sm text-white hover:bg-gray-800"
          >
            Cerrar bolsa{activeBag.courier ? ` ${activeBag.courier.name}` : ''}
          </button>
        </Can>
      </div>
      <Can cap="warehouse.operate">
        <BagScanForm
          placeholder="Escaneá otra bolsa para cambiar..."
          color="#374151"
          onScan={(code) => onScan(code)}
        />
      </Can>
    </div>
  );
}
